import {
  ArcGISRequestError,
  FieldType,
  IField,
} from '@esri/arcgis-rest-request';
import {
  CalciteInputText,
  CalciteButton,
  CalciteLabel,
  CalciteSelect,
  CalciteOption,
  CalciteInputNumber,
  CalciteInputMessage,
  CalciteSwitch,
} from '@esri/calcite-components-react';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { undefBool, undefBoolObj } from '../../util/webComponentUtils';
import {
  useForm,
  FieldError,
  Controller,
  ControllerProps,
  useWatch,
  useFormState,
  Control,
} from 'react-hook-form';

import { esriTypeLookup } from '../../consts/esriTypeInfo';
import { useAccountManager } from '../../contexts/AccountManagerContext';
import { updateField } from '../../util/layerUtils';
import { EsriTypeEditor } from '../GridComponents/Editors/EsriTypeEditor';
import { toast } from 'react-toastify';
import { ILayerDefinition } from '@esri/arcgis-rest-feature-service';

type FieldEditorProps = {
  layerUrl: string;
  mode: 'create' | 'edit' | 'view';
  onFieldCreated?: (field: IField) => void;
  field?: IFieldWithDescription;
  layerDefinition?: ILayerDefinition;
} & (
  | {
      mode: 'edit' | 'view';
      field: IFieldWithDescription;
    }
  | {
      mode: 'create';
      layerDefinition: ILayerDefinition;
    }
);

export type FieldEditorInputs = {
  name: string;
  description: string;
  alias: string;
  type: FieldType;
  length: number;
  defaultValue: string;
  nullable: boolean;
  editable: boolean;
};

const inputStatus = (error: FieldError | undefined) =>
  error ? 'invalid' : 'idle';

const SubmitButton = ({
  control,
  loading,
  onSubmit,
  onReset,
}: {
  control: Control<FieldEditorInputs>;
  loading: boolean;
  onSubmit: () => void;
  onReset: () => void;
}) => {
  const { isDirty } = useFormState({ control });
  return (
    <div className="flex justify-between flex-row-reverse items-end">
      <CalciteButton
        {...undefBoolObj(loading, 'loading')}
        onClick={onSubmit}
        disabled={undefBool(!isDirty)}
      >
        Save
      </CalciteButton>
      {isDirty && (
        <CalciteButton
          {...undefBoolObj(loading, 'loading')}
          kind="danger"
          iconStart="reset"
          disabled={undefBool(!isDirty)}
          onClick={onReset}
        >
          Reset
        </CalciteButton>
      )}
    </div>
  );
};

const LabelTitle = ({
  text,
  required = false,
}: {
  text: string;
  required?: boolean;
}) => (
  <span>
    {text}
    {required && <span className="text-danger">*</span>}
  </span>
);

const FieldEditorInput = <T extends keyof FieldEditorInputs>({
  label,
  inline,
  name,
  ...controllerProps
}: {
  label: string;
  inline?: boolean;
  name: T;
} & ControllerProps<FieldEditorInputs, T>) => (
  <Controller
    name={name}
    {...controllerProps}
    render={(renderProps) => {
      const errorMessage = renderProps.fieldState.error && (
        <CalciteInputMessage status="invalid" icon="frown">
          {renderProps.fieldState.error.message}
        </CalciteInputMessage>
      );
      return (
        <CalciteLabel layout={inline ? 'inline-space-between' : 'default'}>
          <LabelTitle text={label} />
          {inline && errorMessage}
          {controllerProps.render(renderProps)}
          {!inline && errorMessage}
        </CalciteLabel>
      );
    }}
  />
);

export const FieldEditor: React.FC<FieldEditorProps> = ({
  layerUrl,
  onFieldCreated,
  mode,
  field,
  layerDefinition,
}) => {
  const { accountManager } = useAccountManager();

  const defaultDescription = field?.description ?? '{"value": null}';

  const defaultValues: FieldEditorInputs = {
    name: field?.name ?? '',
    description: JSON.parse(defaultDescription).value ?? '',
    nullable: field?.nullable ?? true,
    editable: field?.editable ?? true,
    length: field?.length ?? 64,
    defaultValue: field?.defaultValue ?? '',
    type: field?.type ?? 'esriFieldTypeString',
    alias: field?.alias ?? '',
  };

  const { handleSubmit, control, getValues, reset } =
    useForm<FieldEditorInputs>({
      mode: 'all',
      reValidateMode: 'onBlur',
      defaultValues,
    });

  const { mutate: requestAddField, isLoading: newFieldLoading } = useMutation({
    onSuccess: (res, { field }) => {
      if (res.success) onFieldCreated?.(field);
    },
    onError: (err) => {
      if (err instanceof ArcGISRequestError && err.response?.error?.details) {
        toast.error(err.response.error.details[0]);
      }
    },
    mutationFn: updateField,
  });

  const fieldTypeWatch = useWatch({ control, name: 'type' });

  const onSubmit = handleSubmit((data) => {
    const values = getValues();

    const newField: IFieldWithDescription = {
      ...data,
      description: JSON.stringify({
        value: values.description,
      }),
    };

    if (mode === 'view' || !accountManager) return;

    requestAddField({
      field: newField,
      mode,
      layerUrl,
      accountManager,
    });
  });

  return (
    <div className="p-2">
      <FieldEditorInput
        label="Name"
        name="name"
        control={control}
        rules={{
          required: 'Name is required',
          minLength: {
            value: 2,
            message: 'Name must be at least 2 characters',
          },
          maxLength: {
            value: 64,
            message: 'Name must be less than 64 characters',
          },
          pattern: {
            value: /^(\w+)$/,
            message: 'Name must only contain letters, numbers, and underscores',
          },
          validate: {
            noDuplicates: async (value) => {
              if (mode === 'create') {
                const fields = layerDefinition.fields ?? [];
                if (fields?.some((f) => f.name === value))
                  return 'Name must be a unique value';
              }
            },
          },
        }}
        render={({ field: { onChange, ...props }, fieldState }) => (
          <CalciteInputText
            {...props}
            onCalciteInputTextInput={onChange}
            status={inputStatus(fieldState.error)}
            readOnly={undefBool(mode === 'view' || mode === 'edit')}
          />
        )}
      />

      <FieldEditorInput
        label="Alias"
        name="alias"
        control={control}
        rules={{
          required: { value: true, message: 'Field alias is required' },
          pattern: {
            value: /^[^<>]*$/i,
            message: 'Field alias cannot contain < or >',
          },
        }}
        defaultValue=""
        render={({ field: { onChange, ...props }, fieldState: { error } }) => (
          <CalciteInputText
            {...props}
            onCalciteInputTextInput={onChange}
            status={inputStatus(error)}
            readOnly={undefBool(mode === 'view')}
          />
        )}
      />

      <FieldEditorInput
        label="Description"
        name="description"
        control={control}
        defaultValue=""
        render={({ field: { onChange, ...props }, fieldState: { error } }) => (
          <CalciteInputText
            {...props}
            onCalciteInputTextInput={onChange}
            status={inputStatus(error)}
            readOnly={undefBool(mode === 'view')}
          />
        )}
      />

      <FieldEditorInput
        inline
        label="Type"
        name="type"
        control={control}
        defaultValue="esriFieldTypeString"
        render={({ field: { onChange, ...props } }) => (
          <CalciteSelect
            label="select-field-type"
            {...props}
            onCalciteSelectChange={onChange}
            defaultValue={props.value}
          >
            {Object.entries(esriTypeLookup).map(([type, { name }]) => (
              <CalciteOption key={type} value={type}>
                {name}
              </CalciteOption>
            ))}
          </CalciteSelect>
        )}
      />
      {fieldTypeWatch === 'esriFieldTypeString' && (
        <FieldEditorInput
          label="Length"
          inline
          name="length"
          control={control}
          defaultValue={64}
          rules={{ max: { value: 4000, message: 'Max length is 4000' } }}
          render={({
            field: { onChange, value, ...props },
            fieldState: { error },
          }) => (
            <CalciteInputNumber
              {...props}
              value={value.toString()}
              alignment="end"
              className="w-32"
              status={inputStatus(error)}
              onCalciteInputNumberChange={onChange}
              readOnly={undefBool(mode === 'view')}
            />
          )}
        />
      )}

      <FieldEditorInput
        label="Nullable"
        inline
        name="nullable"
        control={control}
        render={({ field: { onChange, value, ...props } }) => (
          <CalciteSwitch
            {...props}
            onCalciteSwitchChange={onChange}
            checked={undefBool(value)}
            disabled={undefBool(mode === 'view')}
          />
        )}
      />

      <FieldEditorInput
        label="Editable"
        inline
        name="editable"
        control={control}
        render={({ field: { onChange, value, ...props } }) => (
          <CalciteSwitch
            {...props}
            onCalciteSwitchChange={onChange}
            checked={undefBool(value)}
            disabled={undefBool(mode === 'view')}
          />
        )}
      />

      <FieldEditorInput
        label="Default value"
        name="defaultValue"
        control={control}
        render={({ field: { onChange, value } }) => (
          <EsriTypeEditor
            type={fieldTypeWatch}
            onChange={onChange}
            value={value}
            readOnly={undefBool(mode === 'view')}
          />
        )}
      />
      {mode !== 'view' && (
        <SubmitButton
          control={control}
          onSubmit={onSubmit}
          onReset={() => reset(defaultValues)}
          loading={newFieldLoading}
        />
      )}
    </div>
  );
};
